import React, { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';

import LoadingSpinner from './LoadingSpinner';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const RENDER_HEIGHT = 800;

const PdfPreview = ({ uri, loading, onReady, onError }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const callbacksRef = useRef({ onReady, onError });
  const [rendering, setRendering] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);

  useEffect(() => {
    callbacksRef.current = { onReady, onError };
  });

  useEffect(() => {
    if (!uri) {
      setHasRendered(false);
      return;
    }

    let cancelled = false;
    setRendering(true);

    pdfjs.getDocument(uri).promise
      .then((pdfDocument) => pdfDocument.getPage(1))
      .then((page) => {
        if (cancelled) { return null; }

        const baseViewport = page.getViewport({ scale: 1.0 });
        const viewport = page.getViewport({ scale: RENDER_HEIGHT / baseViewport.height });

        // Render offscreen so the visible canvas keeps the previous
        // image until the new page is fully drawn.
        const offscreen = window.document.createElement('canvas');
        offscreen.height = viewport.height;
        offscreen.width = viewport.width;

        renderTaskRef.current = page.render({ canvasContext: offscreen.getContext('2d'), viewport });
        return renderTaskRef.current.promise.then(() => offscreen);
      })
      .then((result) => {
        if (cancelled || result === null || !canvasRef.current) { return; }

        canvasRef.current.height = result.height;
        canvasRef.current.width = result.width;
        canvasRef.current.getContext('2d').drawImage(result, 0, 0);

        setHasRendered(true);
        setRendering(false);
        if (callbacksRef.current.onReady) { callbacksRef.current.onReady(); }
      })
      .catch((error) => {
        if (cancelled || error?.name === 'RenderingCancelledException') { return; }

        setRendering(false);
        if (callbacksRef.current.onError) { callbacksRef.current.onError(error); }
      });

    return () => {
      cancelled = true;
      if (renderTaskRef.current) { renderTaskRef.current.cancel(); }
    };
  }, [uri]);

  const busy = loading || rendering;

  return (
    <div className="pdf-preview">
      <canvas
        ref={canvasRef}
        className={busy ? 'pdf-preview-canvas pdf-preview-dimmed' : 'pdf-preview-canvas'}
        hidden={!hasRendered}
      />
      { !hasRendered &&
        <LoadingSpinner size="xxSmall" statusText="Loading Preview" />
      }
      { hasRendered && busy &&
        <div className="pdf-preview-overlay">
          <LoadingSpinner size="xxSmall" statusText="Loading Preview" />
        </div>
      }
    </div>
  );
};

export default PdfPreview;
